// Data layer. Pulls the Supabase views/tables and derives the investor
// metrics that are NOT in the spreadsheets (per-bed economics, ARR from
// year-average MRR, revenue-retention, occupancy %, annual financials,
// turnover). The spreadsheets hold raw inputs only.
import { sb, sbAll } from "./supabase.js";

export const REGISTERED_BEDS = 28;

// LaingBuisson / Knight Frank 2024 — annual, per bed.
export const BENCH = {
  revPerBed: 52000,
  ebitdaPerBed: 11960,
  gpPerBed: 17650,
  wagesPerBed: 26000,
  ebitdaMarginPct: 23,        // 11,960 / 52,000
  turnoverPct: 30,            // UK social-care sector average (Skills for Care)
};
export const BENCH_REV_PER_BED = BENCH.revPerBed;
export const BENCH_EBITDA_PER_BED = BENCH.ebitdaPerBed;

// Manually maintained — NOT in the data room (no machine-readable source).
// Update here when the founder confirms new figures.
export const PAY = { min: 16.20, avg: 17.11 };
export const COMPANY = { founded: 2014 };

export const MILESTONES = [
  { year: 2014, tag: "Founded",
    text: "Acquisition, redevelopment & commissioning of a 28-bed care home" },
  { year: 2014, tag: "100% Occupancy",
    text: "Achieved full occupancy within ~3 months of launch" },
  { year: 2021, tag: "COVID Response",
    text: "Multi-site crisis management at Local Authority request for an "
        + "unaffiliated home" },
  { year: 2022, tag: "Power List",
    text: "Care Home Professional: Leaders of Tomorrow" },
  { year: 2023, tag: "Industry Recognition",
    text: "Care Home Professional Power List: Innovators in Care" },
  { year: 2025, tag: "HealthInvestor",
    text: "Power List: One to Watch" },
  { year: 2025, tag: "Advisory Roles",
    text: "Mentored / advised CEOs of 6-home, 11-home and 65-home groups" },
  { year: 2026, tag: "Staff Retention",
    text: "0% employee turnover for 12 month period" },
];

export async function loadAll() {
  const [
    kpis, fin, pl, occMonthly, occAnnual, daily,
    residents, falls, fallsRoll, illness, behaviour, deceased, staff, annualRev, mrrStats,
    perBed, cagr, turnover, webDaily, webTop,
  ] = await Promise.all([
    sb("v_financial_kpis?select=*&order=month.asc"),
    sb("financial_metrics?select=*&order=month.asc"),
    sb("pl_line_items?select=*"),
    sb("v_occupancy_monthly?select=*&order=month.asc"),
    sb("v_occupancy_annual?select=*&order=fy_start_year.asc"),
    sbAll("daily_occupancy?select=date,beds_occupied&order=date.asc"),
    sb("residents?select=*&order=room.asc"),
    sb("falls_monthly?select=*"),
    sb("v_falls_rolling_avg?select=*&order=period.asc"),
    sb("illness_monthly?select=*"),
    sb("behaviour_monthly?select=*"),
    sb("deceased_monthly?select=*"),
    sb("staff_monthly?select=*"),
    sb("annual_revenue?select=*&order=year.asc"),
    sb("mrr_stats?select=*&order=year.asc"),
    sb("per_bed_analysis?select=*"),
    sb("cagr_stats?select=*"),
    sb("staff_turnover_monthly?select=*&order=period.asc"),
    sb("web_analytics_daily?select=*&order=date.asc"),
    sbAll("web_analytics_top?select=*&order=date.asc"),
  ]);
  const dailyAsc = [...daily].sort((a, b) => a.date.localeCompare(b.date));
  return {
    kpis, fin, pl, occMonthly, occAnnual,
    daily: dailyAsc, residents, falls, fallsRoll, illness, behaviour, deceased,
    staff, annualRev, mrrStats, perBed, cagr: cagr[0] || null, turnover,
    webDaily, webTop,
  };
}

const num = (v) => (v == null || isNaN(+v) ? 0 : +v);
export const gbp0 = (v) =>
  v == null ? "—" : "£" + Math.round(num(v)).toLocaleString("en-GB");
export const gbpM = (v) =>
  v == null ? "—" : "£" + (num(v) / 1e6).toFixed(2) + "M";
export const gbpCompact = (v) => {
  if (v == null) return "—";
  const n = num(v);
  if (Math.abs(n) >= 1e6) return "£" + (n / 1e6).toFixed(2) + "M";
  if (Math.abs(n) >= 1e3) return "£" + (n / 1e3).toFixed(1) + "K";
  return "£" + Math.round(n).toLocaleString("en-GB");
};
export const pct1 = (v) => (v == null ? "—" : num(v).toFixed(1) + "%");
export const pct2 = (v) => (v == null ? "—" : num(v).toFixed(2) + "%");
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Financial-year month order: Apr → Mar.
const FY_MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
export const fyLabel = (y) => `${y}-${String(y + 1).slice(-2)}`;
export const mlabel = (s) => {
  if (!s) return "";
  const [y, m] = s.split("-");
  return `${MON[+m - 1]} ${y.slice(2)}`;
};

// Financial year (1 Apr → 31 Mar) the date string falls in.
function fyStart(dateStr) {
  const d = new Date(dateStr);
  const y = d.getUTCFullYear();
  return d.getUTCMonth() >= 3 ? y : y - 1;
}

export function derive(d) {
  const fin = d.fin;
  const last = fin[fin.length - 1] || {};
  const lastMonth = last.month;

  // ── Annual financials from the Per Bed Analysis sheet ──────────────
  // Values in per_bed_analysis are per-bed-per-annum exactly as the sheet
  // computes them; ×28 gives the home total. This is the authoritative
  // annual series (back to FY 2022-23) — never recomputed from monthlies.
  const pbCat = (cat, fy) => {
    const r = d.perBed.find((x) => x.category === cat && x.fy === fy);
    return r ? num(r.value) : null;
  };
  const fys = [...new Set(d.perBed.map((x) => x.fy))].sort();
  const annual = fys.map((fy) => {
    // "Total Income" is only backfilled once a FY closes — it sits at 0 for
    // the in-progress year until then. "Sales" is fed live and matches
    // Total Income exactly in every closed year, so it's a safe stand-in.
    const rpb = pbCat("Total Income", fy) || pbCat("Sales", fy);
    const epb = pbCat("Adjusted EBITDA", fy) ?? pbCat("EBITDA", fy);
    const gpb = pbCat("Gross Profit", fy);
    const wpb = pbCat("Direct Wages", fy);
    // Determine ytd from dates: FY "YYYY-YY" (Oct–Sep) ends Sep of the second
    // year; it's incomplete until Oct 1 of that year.
    const fyYtd = (() => {
      const m2 = fy.match(/(\d{4})-(\d{2})/);
      if (!m2) return /ytd/i.test(fy);
      const endYear = +m2[1] + 1;
      return new Date() < new Date(`${endYear}-10-01`);
    })();
    return {
      fy, ytd: fyYtd,
      revPerBed: rpb, ebitdaPerBed: epb, gpPerBed: gpb, wagesPerBed: wpb,
      revenue: rpb == null ? null : rpb * REGISTERED_BEDS,
      ebitda: epb == null ? null : epb * REGISTERED_BEDS,
      ebitdaMarginPct: rpb ? (epb / rpb) * 100 : null,
    };
  });
  const fullYears = annual.filter((a) => !a.ytd && a.revPerBed != null);
  const pbLatest = fullYears[fullYears.length - 1]
    || annual[annual.length - 1] || {};

  // ── MRR: average of the last 12 months in the management accounts ──
  // (cagr.xlsx's MRR table is hand-kept and lags by months; the monthly P&L
  // is what the bookkeeper updates, so it is the fresher source.)
  const ms = [...d.mrrStats].sort((a, b) => a.year - b.year);
  const msPrev = ms[ms.length - 2] || {};
  const mrr12 = fin.slice(-12).map((r) => num(r.mrr)).filter((v) => v);
  const mrrYearAvg = mrr12.length
    ? mrr12.reduce((t, v) => t + v, 0) / mrr12.length
    : num((ms[ms.length - 1] || {}).avg_mrr);
  const arr = mrrYearAvg * 12;
  // Revenue retention = this year's avg MRR vs last year's avg MRR.
  const nrr = num(msPrev.avg_mrr)
    ? (mrrYearAvg / num(msPrev.avg_mrr)) * 100 : null;

  // ── Occupancy snapshot from the daily series ───────────────────────
  const today = d.daily[d.daily.length - 1] || null;
  const curBeds = today ? num(today.beds_occupied) : 0;
  const curFy = today ? fyStart(today.date) : null;
  const curMonth = today ? today.date.slice(0, 7) : null;
  const mtd = d.daily.filter((r) => r.date.slice(0, 7) === curMonth);
  const ytd = d.daily.filter((r) => fyStart(r.date) === curFy);
  const avg = (a) =>
    a.length ? a.reduce((s, r) => s + num(r.beds_occupied), 0) / a.length : 0;
  const ytdAvgBeds = avg(ytd);
  const occYtdPct = (ytdAvgBeds / REGISTERED_BEDS) * 100;
  const daysAtFull = ytd.filter(
    (r) => num(r.beds_occupied) >= REGISTERED_BEDS).length;

  // ── Revenue mix from the current resident fee list ─────────────────
  const FUND = { "Private": "Private", "NCC Private": "Private",
    "Newport": "Local Authority", "Cardiff": "Local Authority",
    "3rd Party": "Top-up" };
  const mix = {};
  for (const r of d.residents) {
    const k = FUND[r.funding_type] || "Other";
    mix[k] = (mix[k] || 0) + num(r.weekly_fee);
  }
  const mixTotal = Object.values(mix).reduce((s, v) => s + v, 0) || 1;
  const revenueMix = Object.entries(mix).map(([k, v]) => ({
    name: k, weekly: v, pct: (v / mixTotal) * 100,
  })).sort((a, b) => b.weekly - a.weekly);

  // ── Staffing headcount (latest period in staff_monthly) ────────────
  const periods = [...new Set(d.staff.map((s) => s.period))].sort();
  const lastStaffPeriod = periods[periods.length - 1];
  const staffNow = d.staff.filter((s) => s.period === lastStaffPeriod);

  // ── Turnover: average MONTHLY rate, by financial year ──────────────
  // monthly rate = separations ÷ average headcount that month (the sheet's
  // own definition). Reported as the rolling-12-month average, and as a
  // per-FY series for the trend bars.
  const tov = [...d.turnover]
    .filter((r) => r.headcount_start != null || r.headcount_end != null)
    .map((r) => {
      const hc = ((num(r.headcount_start) + num(r.headcount_end)) / 2) || 0;
      return {
        period: r.period,
        fy: fyStart(r.period),
        rate: hc ? (num(r.separations) / hc) * 100 : 0,
        sep: num(r.separations),
      };
    });
  const fyTov = {};
  for (const t of tov) (fyTov[t.fy] ||= []).push(t.rate);
  const turnoverByFy = Object.entries(fyTov)
    .map(([fy, arr2]) => {
      const v = arr2.reduce((s, x) => s + x, 0) / arr2.length;
      return {
        fyStart: +fy, label: `Apr ${+fy + 1}`, months: arr2.length,
        value: +v.toFixed(2),
        band: v <= 1 ? "low" : v <= 2 ? "mid" : "high",
      };
    })
    .sort((a, b) => a.fyStart - b.fyStart);
  // Days since the last recorded leaver. The source is monthly, so the exact
  // date within the month is unknown — count from the first day of the month
  // AFTER the last separation, which is the earliest date the run is certain
  // to have started. Understates the streak rather than overstating it.
  const lastSepRow = [...tov]
    .sort((a, b) => a.period.localeCompare(b.period)).reverse()
    .find((r) => r.sep > 0) || null;
  let daysWithoutLeaver = null, leaverFreeSince = null;
  if (lastSepRow) {
    const [y, mo] = lastSepRow.period.split("-").map(Number);
    const start = new Date(Date.UTC(mo === 12 ? y + 1 : y, mo % 12, 1));
    leaverFreeSince = start;
    daysWithoutLeaver = Math.max(0, Math.floor(
      (Date.now() - start.getTime()) / 86400000));
  }

  const last12 = tov.slice(-12);
  // Round DOWN to 2dp — report the conservative figure (0.59%, not 0.60%).
  const turnoverTtm = last12.length
    ? Math.floor((last12.reduce((s, x) => s + x.rate, 0)
        / last12.length) * 100) / 100
    : null;
  const headcount = staffNow.length
    || (d.turnover.length
      ? num(d.turnover[d.turnover.length - 1].headcount_end) : 0);

  // ── Clinical: latest falls month, YoY, spike rule ──────────────────
  const fSorted = [...d.falls].sort(
    (a, b) => a.year - b.year || a.month_num - b.month_num);
  const fLast = fSorted[fSorted.length - 1] || null;
  const fYoYprev = fLast
    ? d.falls.find((x) => x.year === fLast.year - 1
        && x.month_num === fLast.month_num)
    : null;
  const fallsYoY = fLast && fYoYprev && num(fYoYprev.count)
    ? ((num(fLast.count) - num(fYoYprev.count)) / num(fYoYprev.count)) * 100
    : null;
  const last3 = fSorted.slice(-4, -1);
  const avg3 = last3.length
    ? last3.reduce((s, x) => s + num(x.count), 0) / last3.length : 0;
  const fallsSpike = fLast && avg3 > 0 && num(fLast.count) > 2 * avg3;
  const dLast = [...d.deceased].sort(
    (a, b) => a.year - b.year || a.month_num - b.month_num).slice(-1)[0];

  // Illness/behaviour — same latest-month pattern as falls. New for the
  // operational metrics briefing project (2026-08); data starts accumulating
  // from whenever that pipeline's weekly runs began, so early months are
  // sparse/absent by design, not a data-quality issue.
  const iSorted = [...d.illness].sort(
    (a, b) => a.year - b.year || a.month_num - b.month_num);
  const illnessLast = iSorted[iSorted.length - 1] || null;
  const bSorted = [...d.behaviour].sort(
    (a, b) => a.year - b.year || a.month_num - b.month_num);
  const behaviourLast = bSorted[bSorted.length - 1] || null;

  const cagrPct = d.cagr ? num(d.cagr.cagr_pct) * 100 : null;
  const totalGrowthPct = d.cagr ? num(d.cagr.total_growth) * 100 : null;

  // Year-over-year revenue growth from the audited annual series.
  const ar = [...d.annualRev].sort((a, b) => a.year - b.year);
  const arL = ar[ar.length - 1] || {};
  const arP = ar[ar.length - 2] || {};
  const revYoYPct = num(arP.revenue)
    ? (num(arL.revenue) / num(arP.revenue) - 1) * 100 : null;
  const revYoYYear = arL.year;

  // Falls — current calendar year (2026) summary.
  const yNow = new Date().getFullYear();
  const f2026 = d.falls.filter((f) => f.year === yNow);
  const fallsAvg2026 = f2026.length
    ? f2026.reduce((s, x) => s + num(x.count), 0) / f2026.length : null;
  const fallsYtd2026 = f2026.reduce((s, x) => s + num(x.count), 0);

  // ── Investment-case metrics (the narrative spine) ──────────────────
  // Self-funded share = Private (incl. NCC Private) of weekly fee income.
  const selfRow = revenueMix.find((r) => r.name === "Private");
  const selfFundedPct = selfRow ? selfRow.pct : null;

  // Occupancy resilience from the annual view.
  const oa = [...d.occAnnual].sort((a, b) => a.fy_start_year - b.fy_start_year);
  const occPcts = oa.map((r) => num(r.occ_pct)).filter((v) => v > 0);
  const occResilLo = occPcts.length ? Math.min(...occPcts) : null;
  const occResilHi = occPcts.length ? Math.max(...occPcts) : null;
  const bestFull = oa.reduce(
    (b, r) => (num(r.days_at_full) > num(b?.days_at_full || 0) ? r : b), null);

  // Falls reduction — monthly average, base year (2020 if present) vs the
  // latest COMPLETE calendar year for the headline, with the in-progress
  // year reported separately as YTD. Computed from the actual data, never
  // a target figure.
  const fy = {};
  for (const f of d.falls) (fy[f.year] ||= []).push(num(f.count));
  const fYears = Object.keys(fy).map(Number).sort((a, b) => a - b);
  const favg = (y) =>
    fy[y] && fy[y].length ? fy[y].reduce((s, x) => s + x, 0) / fy[y].length : 0;
  const calNow = new Date().getFullYear();
  const fallsBaseYear = fYears.includes(2020) ? 2020 : fYears[0];
  const fullYrs = fYears.filter((y) => y < calNow);
  const fallsRedFullYear = fullYrs.length
    ? fullYrs[fullYrs.length - 1] : fYears[fYears.length - 1];
  const fallsLateYear = fYears[fYears.length - 1];
  const baseAvg = favg(fallsBaseYear);
  const fallsRedPct = baseAvg
    ? ((favg(fallsRedFullYear) - baseAvg) / baseAvg) * 100 : null;
  const fallsRedYtdPct = baseAvg
    ? ((favg(fallsLateYear) - baseAvg) / baseAvg) * 100 : null;

  // ── Occupancy by financial year — derived from the daily series ────
  // Powers the Annual Overview, the 100%-occupancy analysis and the
  // monthly-rate matrix. Recomputes on every sync; no manual input.
  const REG = REGISTERED_BEDS;
  const byFy = {};
  for (const r of d.daily) {
    (byFy[fyStart(r.date)] ||= []).push(r);
  }
  const lastDaily = d.daily[d.daily.length - 1];
  const occFy = Object.keys(byFy).map(Number).sort((a, b) => a - b)
    .map((y) => {
      const rows = byFy[y];
      const beds = rows.map((r) => num(r.beds_occupied));
      const days = beds.length;
      const mean = beds.reduce((s, b) => s + b, 0) / days;
      const variance = beds.reduce((s, b) => s + (b - mean) ** 2, 0) / days;
      const monMean = {};
      const monBeds = {};
      for (const r of rows) {
        const mn = +r.date.slice(5, 7);
        (monBeds[mn] ||= []).push(num(r.beds_occupied));
      }
      for (const mn of Object.keys(monBeds)) {
        const a = monBeds[mn];
        monMean[mn] = a.reduce((s, b) => s + b, 0) / a.length;
      }
      const mMeans = Object.values(monMean);
      const monRate = {};
      for (const mn of Object.keys(monMean)) {
        monRate[mn] = (monMean[mn] / REG) * 100;
      }
      const rates = Object.values(monRate);
      const fyEnd = `${y + 1}-03-31`;
      const ytd = rows[rows.length - 1].date < fyEnd
        || (lastDaily && fyStart(lastDaily.date) === y
            && lastDaily.date < fyEnd);
      const daysAtFull = beds.filter((b) => b >= REG).length;
      return {
        fy: y, label: fyLabel(y), ytd,
        regBeds: REG,
        avgDailyOcc: +mean.toFixed(2),
        avgOccRate: +((mean / REG) * 100).toFixed(1),
        peakMonthlyAvg: +Math.max(...mMeans).toFixed(2),
        troughMonthlyAvg: +Math.min(...mMeans).toFixed(2),
        monthlyRange: +(Math.max(...mMeans) - Math.min(...mMeans)).toFixed(2),
        stdDev: +Math.sqrt(variance).toFixed(2),
        daysAtFull, daysTracked: days,
        fullRatePct: +((daysAtFull / days) * 100).toFixed(1),
        avgEmptyBeds: +(REG - mean).toFixed(2),
        bestRatePct: +Math.max(...rates).toFixed(1),
        monthRate: monRate,
      };
    });

  // FY × month occupancy-rate matrix (Apr → Mar) + Full Year + Best.
  const occMatrix = occFy.map((f) => {
    const cells = {};
    for (const mn of FY_MONTHS) {
      cells[mn] = f.monthRate[mn] != null
        ? +f.monthRate[mn].toFixed(1) : null;
    }
    return {
      label: f.label, ytd: f.ytd, cells,
      full: f.avgOccRate, best: f.bestRatePct,
    };
  });

  // Falls by month × year, with per-year Total and Average rows.
  const fYearsAsc = fYears;
  const fGrid = {};
  for (const f of d.falls) (fGrid[f.year] ||= {})[f.month_num] = num(f.count);
  const fallsMatrix = {
    years: fYearsAsc.map((y) => ({
      year: y, ytd: y >= calNow,
      total: Object.values(fGrid[y] || {}).reduce((s, x) => s + x, 0),
      avg: +favg(y).toFixed(1),
    })),
    rows: MON.map((mn, i) => ({
      month: mn,
      cells: Object.fromEntries(
        fYearsAsc.map((y) => [y, fGrid[y]?.[i + 1] ?? null])),
    })),
  };

  // Falls seasonality — average falls per calendar month over the last three
  // calendar years (incl. the current one to date). Older years skew it
  // (Jan 2020); averaging keeps months with fewer years of data comparable.
  const seasonFrom = calNow - 2;
  const seasonAgg = {};
  for (const f of d.falls) {
    if (f.year < seasonFrom) continue;
    (seasonAgg[f.month_num] ||= { total: 0, n: 0 });
    seasonAgg[f.month_num].total += num(f.count);
    seasonAgg[f.month_num].n += 1;
  }
  const fallsSeasonality = MON.map((mn, i) => {
    const s = seasonAgg[i + 1] || { total: 0, n: 0 };
    const avg = s.n ? +(s.total / s.n).toFixed(1) : 0;
    return { x: i + 1, month: mn, y: avg, z: avg || 0.1, total: s.total, n: s.n };
  });
  // Median of the unrounded averages; round only the result.
  const seasonVals = Object.values(seasonAgg).filter((s) => s.n)
    .map((s) => s.total / s.n).sort((a, b) => a - b);
  const sm = seasonVals.length >> 1;
  const seasonMed = !seasonVals.length ? 0 : +(seasonVals.length % 2
    ? seasonVals[sm] : (seasonVals[sm - 1] + seasonVals[sm]) / 2).toFixed(1);

  return {
    lastMonth,
    // headline financials
    mrrYearAvg, arr, nrr,
    annual, pbLatest,
    revPerBed: pbLatest.revPerBed, ebitdaPerBed: pbLatest.ebitdaPerBed,
    ebitdaMarginAnnualPct: pbLatest.ebitdaMarginPct,
    cagrPct, totalGrowthPct,
    revYoYPct, revYoYYear,
    fallsAvg2026, fallsYtd2026, fallsYear: yNow,
    selfFundedPct,
    occResilLo, occResilHi,
    bestDaysFull: bestFull ? num(bestFull.days_at_full) : null,
    bestDaysFullFy: bestFull ? bestFull.fy_start_year : null,
    fallsRedPct, fallsRedYtdPct,
    fallsBaseYear, fallsRedFullYear, fallsLateYear,
    fallsMatrix, fallsSeasonality, seasonMed, seasonFrom,
    // occupancy
    curBeds, curMonth,
    occupancyPct: (curBeds / REGISTERED_BEDS) * 100,
    occYtdPct, ytdAvgBeds, daysAtFull,
    occFy, occMatrix,
    revenueMix,
    // staffing
    headcount, lastStaffPeriod,
    turnoverByFy, turnoverTtm, daysWithoutLeaver, leaverFreeSince,
    // clinical
    fLast, fallsYoY, fallsSpike, deceasedLast: dLast,
    illnessLast, behaviourLast,
  };
}
